import React, { useEffect, useRef, useState } from 'react';
import {
  Bold, Italic, Underline, Strikethrough, Heading3, List, ListOrdered,
  Quote, Link2, RemoveFormatting, Code2,
} from 'lucide-react';

const QUICK_EMOJI = ['✅', '💡', '🔶', '🔷', '🎁', '♥️', '🌶️', '📸', '⭐', '1️⃣', '2️⃣', '3️⃣'];

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

/**
 * 輕量所見即所得編輯器（零依賴）：工具列（粗體/斜體/底線/刪除線/標題/
 * 清單/引述/連結/清除格式）＋ emoji 快捷＋ HTML 原始碼切換。
 * 內容經上游 SafeHtml 淨化後渲染，此處僅管理 HTML 字串。
 */
export default function RichTextEditor({ value, onChange, placeholder = '', minHeight = 180 }: RichTextEditorProps) {
  const [mode, setMode] = useState<'wysiwyg' | 'html'>('wysiwyg');
  const [htmlModeText, setHtmlModeText] = useState('');
  const editRef = useRef<HTMLDivElement>(null);
  const lastEmitted = useRef<string>(value);

  // 外部 value 變化（表單載入/重置）→ 同步進編輯區
  useEffect(() => {
    if (mode === 'wysiwyg' && editRef.current && value !== editRef.current.innerHTML) {
      editRef.current.innerHTML = value || '';
    }
    lastEmitted.current = value;
  }, [value, mode]);

  const emit = () => {
    if (!editRef.current) return;
    const html = editRef.current.innerHTML;
    lastEmitted.current = html;
    onChange(html === '<br>' || html === '<div><br></div>' ? '' : html);
  };

  const exec = (command: string, arg?: string) => {
    editRef.current?.focus();
    document.execCommand(command, false, arg);
    emit();
  };

  const insertText = (text: string) => {
    editRef.current?.focus();
    document.execCommand('insertText', false, text);
    emit();
  };

  const addLink = () => {
    const url = window.prompt('連結網址（https://…）');
    if (!url) return;
    exec('createLink', url.startsWith('http') ? url : `https://${url}`);
  };

  const switchToHtml = () => {
    if (editRef.current) setHtmlModeText(editRef.current.innerHTML);
    setMode('html');
  };

  const switchToWysiwyg = () => {
    setMode('wysiwyg');
  };

  const btn = 'w-8 h-8 flex items-center justify-center rounded hover:bg-stone-200 dark:hover:bg-gray-600 text-stone-700 dark:text-gray-200 transition';
  const btnActive = 'w-8 h-8 flex items-center justify-center rounded bg-stone-300 dark:bg-gray-500 text-stone-900 dark:text-white';

  return (
    <div className="border border-stone-300 dark:border-gray-600 rounded-xl overflow-hidden bg-white dark:bg-gray-900">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 flex-wrap px-2 py-1.5 bg-stone-100 dark:bg-gray-800 border-b border-stone-200 dark:border-gray-700">
        <button type="button" className={btn} title="粗體" onMouseDown={(e) => { e.preventDefault(); exec('bold'); }}><Bold size={15} /></button>
        <button type="button" className={btn} title="斜體" onMouseDown={(e) => { e.preventDefault(); exec('italic'); }}><Italic size={15} /></button>
        <button type="button" className={btn} title="底線" onMouseDown={(e) => { e.preventDefault(); exec('underline'); }}><Underline size={15} /></button>
        <button type="button" className={btn} title="刪除線" onMouseDown={(e) => { e.preventDefault(); exec('strikeThrough'); }}><Strikethrough size={15} /></button>
        <span className="w-px h-5 bg-stone-300 dark:bg-gray-600 mx-1" />
        <button type="button" className={btn} title="小標題" onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', '<h3>'); }}><Heading3 size={15} /></button>
        <button type="button" className={btn} title="清單" onMouseDown={(e) => { e.preventDefault(); exec('insertUnorderedList'); }}><List size={15} /></button>
        <button type="button" className={btn} title="數字清單" onMouseDown={(e) => { e.preventDefault(); exec('insertOrderedList'); }}><ListOrdered size={15} /></button>
        <button type="button" className={btn} title="引述" onMouseDown={(e) => { e.preventDefault(); exec('formatBlock', '<blockquote>'); }}><Quote size={15} /></button>
        <button type="button" className={btn} title="連結" onMouseDown={(e) => { e.preventDefault(); addLink(); }}><Link2 size={15} /></button>
        <button type="button" className={btn} title="清除格式" onMouseDown={(e) => { e.preventDefault(); exec('removeFormat'); }}><RemoveFormatting size={15} /></button>
        <span className="w-px h-5 bg-stone-300 dark:bg-gray-600 mx-1" />
        {QUICK_EMOJI.map((em) => (
          <button key={em} type="button" className="w-7 h-8 flex items-center justify-center rounded hover:bg-stone-200 dark:hover:bg-gray-600 text-base" title={`插入 ${em}`} onMouseDown={(e) => { e.preventDefault(); insertText(em); }}>
            {em}
          </button>
        ))}
        <span className="flex-1" />
        <button type="button" className={mode === 'html' ? btnActive : btn} title="HTML 原始碼" onClick={() => (mode === 'html' ? switchToWysiwyg() : switchToHtml())}>
          <Code2 size={15} />
        </button>
      </div>

      {/* Editing surface */}
      {mode === 'wysiwyg' ? (
        <div
          ref={editRef}
          contentEditable
          suppressContentEditableWarning
          role="textbox"
          aria-multiline="true"
          data-placeholder={placeholder}
          onInput={emit}
          onBlur={emit}
          className="rich-editor-area px-4 py-3 outline-none text-sm text-stone-800 dark:text-gray-100 leading-relaxed"
          style={{ minHeight }}
        />
      ) : (
        <textarea
          value={htmlModeText}
          onChange={(e) => { setHtmlModeText(e.target.value); onChange(e.target.value); }}
          spellCheck={false}
          className="w-full px-4 py-3 outline-none text-xs font-mono text-stone-800 dark:text-gray-100 bg-stone-50 dark:bg-gray-900"
          style={{ minHeight }}
          placeholder='<p>支援 p / strong / em / u / s / ul / ol / li / h3 / blockquote / a / img</p>'
        />
      )}

      <style>{`
        .rich-editor-area:empty:before { content: attr(data-placeholder); color: #a8a29e; pointer-events: none; }
        .rich-editor-area h3 { font-size: 1.05rem; font-weight: 700; margin: 0.5rem 0 0.25rem; }
        .rich-editor-area ul, .rich-editor-area ol { padding-left: 1.4rem; list-style: revert; }
        .rich-editor-area ul { list-style: disc; }
        .rich-editor-area ol { list-style: decimal; }
        .rich-editor-area blockquote { border-left: 3px solid #d9381e; padding-left: 0.75rem; color: #78716c; margin: 0.4rem 0; }
        .rich-editor-area a { color: #d9381e; text-decoration: underline; }
        .rich-editor-area img { max-width: 100%; border-radius: 0.5rem; }
      `}</style>
    </div>
  );
}
